import React from 'react';

/**
 * U+2063 INVISIBLE SEPARATOR. A format character, not whitespace, so `trim()` and `\s` leave it
 * alone — a future `.trim()` anywhere on the phrase cannot silently eat a split point.
 */
const SLOT_FENCE = '⁣';

/** Names are upper snake case so a slot can never collide with ordinary translated text. */
const SLOT_PATTERN = /⁣([A-Z0-9_]+)⁣/g;

/**
 * A placeholder to hand `tq` where a styled value belongs.
 *
 * Deliberately brace-free. A marker written into the phrase as i18next syntax does NOT survive:
 * i18next interpolates the result a second time, finds no value of that name, and replaces it with
 * an empty string — so the marker, and the split point with it, disappears.
 *
 * Translators see an ordinary numbered slot and may move it anywhere in their sentence.
 */
export const phraseSlot = (name: string): string => `${SLOT_FENCE}${name}${SLOT_FENCE}`;

/**
 * Puts styled values back into a translated phrase, so the sentence stays ONE translatable unit.
 *
 * The alternative — splitting a sentence into three strings around a bold number — gives translators
 * fragments they cannot reorder, and word order differs by language. This keeps the whole sentence
 * translatable and lets the translator move each value within it.
 *
 * A translation that drops a slot still renders: its value is appended rather than lost, so a bad
 * translation costs the sentence its shape, never the number itself.
 */
export const withPhraseValues = (phrase: string, values: Readonly<Record<string, React.ReactNode>>): React.ReactNode => {
  // A capturing group makes `split` interleave the captures, so odd indexes are the slot names.
  const parts = phrase.split(SLOT_PATTERN);
  const placed = new Set<string>();

  const nodes = parts.map((part, index) => {
    if (index % 2 === 0) return part;
    if (!(part in values)) return null;
    placed.add(part);
    return values[part];
  });

  const missing = Object.keys(values).filter(name => !placed.has(name));

  return (
    <>
      {nodes.map((node, index) => (
        <React.Fragment key={index}>{node}</React.Fragment>
      ))}
      {missing.map(name => (
        <React.Fragment key={name}> {values[name]}</React.Fragment>
      ))}
    </>
  );
};
